/**
 * Keycloak `plyas` client role names (lowercase snake_case).
 * Keep in sync with backend `ResourceAccessRole` and realm-export.
 */

export const ResourceAccessRole = {
  Admin: 'admin',
  // Full application administrator.

  UmaProtection: 'uma_protection',
  // User-Managed Access (UMA) protection API.

  BalancesView: 'balances_view',
  // View balances.

  BalancesCreate: 'balances_create',
  // Create balances.

  BalancesEdit: 'balances_edit',
  // Edit balances.

  BalancesDelete: 'balances_delete',
  // Delete balances.

  BillsView: 'bills_view',
  // View bills.

  BillsCreate: 'bills_create',
  // Create bills.

  BillsEdit: 'bills_edit',
  // Edit bills.

  BillsDelete: 'bills_delete',
  // Delete bills.

  BookingTypesView: 'booking_types_view',
  // View booking types (bookable things: rooms, performers, etc.).

  BookingTypesCreate: 'booking_types_create',
  // Create booking types.

  BookingTypesEdit: 'booking_types_edit',
  // Edit booking types.

  BookingTypesDelete: 'booking_types_delete',
  // Delete booking types.

  BookingsView: 'bookings_view',
  // View bookings.

  BookingsCreate: 'bookings_create',
  // Create bookings.

  BookingsEdit: 'bookings_edit',
  // Edit bookings.

  BookingsDelete: 'bookings_delete',
  // Delete (cancel) bookings.

  BusinessServiceNamesView: 'business_service_names_view',
  // View business service name catalog.

  BusinessServiceNamesCreate: 'business_service_names_create',
  // Create business service names.

  BusinessServiceNamesEdit: 'business_service_names_edit',
  // Edit business service names.

  BusinessServiceNamesDelete: 'business_service_names_delete',
  // Delete business service names.

  BusinessServiceTypesView: 'business_service_types_view',
  // View business service types.

  BusinessServiceTypesCreate: 'business_service_types_create',
  // Create business service types.

  BusinessServiceTypesEdit: 'business_service_types_edit',
  // Edit business service types.

  BusinessServiceTypesDelete: 'business_service_types_delete',
  // Delete business service types.

  SubscriptionTypesView: 'subscription_types_view',
  // View subscription types (the subscription catalog).

  SubscriptionTypesCreate: 'subscription_types_create',
  // Create subscription types.

  SubscriptionTypesEdit: 'subscription_types_edit',
  // Edit subscription types.

  SubscriptionTypesDelete: 'subscription_types_delete',
  // Delete subscription types.

  SubscriptionsView: 'subscriptions_view',
  // View purchased subscriptions (an subscription bought by a person via a payment).

  BusinessServicesView: 'business_services_view',
  // View business services.

  BusinessServicesCreate: 'business_services_create',
  // Create business services.

  BusinessServicesEdit: 'business_services_edit',
  // Edit business services.

  BusinessServicesDelete: 'business_services_delete',
  // Delete business services.

  BusinessServicesClientVisitCountsView: 'business_services_client_visit_counts_view',
  // View per-client visit counts by period (Finances tab report).

  DiseasesView: 'diseases_view',
  // View diseases.

  DiseasesCreate: 'diseases_create',
  // Create diseases.

  DiseasesEdit: 'diseases_edit',
  // Edit diseases.

  DiseasesDelete: 'diseases_delete',
  // Delete diseases.

  EventsView: 'events_view',
  // View events.

  EventsCreate: 'events_create',
  // Create events.

  EventsEdit: 'events_edit',
  // Edit events.

  EventsDelete: 'events_delete',
  // Delete events.

  GroupsView: 'groups_view',
  // View groups (recurring classes).

  GroupsCreate: 'groups_create',
  // Create groups.

  GroupsEdit: 'groups_edit',
  // Edit groups.

  GroupsDelete: 'groups_delete',
  // Delete groups.

  GroupScheduleView: 'group_schedule_view',
  // View a group's weekly schedule slots.

  GroupScheduleCreate: 'group_schedule_create',
  // Add a schedule slot to a group.

  GroupScheduleEdit: 'group_schedule_edit',
  // Edit a group's schedule slot.

  GroupScheduleDelete: 'group_schedule_delete',
  // Remove a schedule slot from a group.

  PaymentsView: 'payments_view',
  // View payments.

  PaymentsCreate: 'payments_create',
  // Create payments.

  PaymentsEdit: 'payments_edit',
  // Edit payments.

  PaymentsDelete: 'payments_delete',
  // Delete payments.

  ClientsView: 'clients_view',
  // View clients (staff).

  ClientsCreate: 'clients_create',
  // Create clients.

  ClientsEdit: 'clients_edit',
  // Edit clients.

  ClientsDelete: 'clients_delete',
  // Delete clients.

  ClientsNegativeBalanceView: 'clients_negative_balance_view',
  // View clients with a negative balance (dedicated accountant/admin/owner-only endpoint, separate from ClientsView).

  Client: 'client',
  // End-user client persona (self-service client account).

  Owner: 'owner',
  // Owner persona / business owner scope.

  ClientView: 'clients_view',
  // Alias of `ClientsView` (same Keycloak role string).

  ClientEdit: 'clients_edit',
  // Alias of `ClientsEdit`.

  ClientDelete: 'clients_delete',
  // Alias of `ClientsDelete`.

  StuffView: 'stuff_view',
  // View stuff resources (staff domain).

  StuffCreate: 'stuff_create',
  // Create staff records.

  StuffEdit: 'stuff_edit',
  // Edit staff records.

  StuffDelete: 'stuff_delete',
  // Delete staff records.

  PerformersStatView: 'performers_stat_view',
  // View performer statistics report (Stuff tab) — distinct from `StuffView`,
  // which gates performer identity lookups; this dedicated stats endpoint
  // uses this role instead.

  PricesView: 'prices_view',
  // View prices.

  PricesCreate: 'prices_create',
  // Create prices.

  PricesEdit: 'prices_edit',
  // Edit prices.

  PricesDelete: 'prices_delete',
  // Delete prices.

  RolesView: 'roles_view',
  // View roles.

  RolesCreate: 'roles_create',
  // Create roles.

  RolesEdit: 'roles_edit',
  // Edit roles.

  RolesDelete: 'roles_delete',
  // Delete roles.

  RulesView: 'rules_view',
  // View rules.

  RulesCreate: 'rules_create',
  // Create rules.

  RulesEdit: 'rules_edit',
  // Edit rules.

  RulesDelete: 'rules_delete',
  // Delete rules.

  SocialNetworkAccountsView: 'social_network_accounts_view',
  // View linked social network accounts.

  SocialNetworkAccountsCreate: 'social_network_accounts_create',
  // Create social network account links.

  SocialNetworkAccountsEdit: 'social_network_accounts_edit',
  // Edit social network account links.

  SocialNetworkAccountsDelete: 'social_network_accounts_delete',
  // Delete social network account links.

  SocialNetworksView: 'social_networks_view',
  // View social networks (catalog).

  SocialNetworksCreate: 'social_networks_create',
  // Create social networks.

  SocialNetworksEdit: 'social_networks_edit',
  // Edit social networks.

  SocialNetworksDelete: 'social_networks_delete',
  // Delete social networks.

  StatTypesView: 'stat_types_view',
  // View statistic types.

  StatTypesCreate: 'stat_types_create',
  // Create statistic types.

  StatTypesEdit: 'stat_types_edit',
  // Edit statistic types.

  StatTypesDelete: 'stat_types_delete',
  // Delete statistic types.

  StatsView: 'stats_view',
  // View statistics.

  StatsCreate: 'stats_create',
  // Create statistics.

  StatsEdit: 'stats_edit',
  // Edit statistics.

  StatsDelete: 'stats_delete',
  // Delete statistics.
} as const

export type ResourceAccessRole = (typeof ResourceAccessRole)[keyof typeof ResourceAccessRole]
// Every allowed Plyas Keycloak client role string (values of the `ResourceAccessRole` const).

export const RESOURCE_ACCESS_ROLE_VALUES: readonly ResourceAccessRole[] = Array.from(
  new Set(Object.values(ResourceAccessRole)),
)
// Deduplicated role strings (aliases collapse to one entry each). Use for runtime checks and tests against UserInfo or token claims.
